import React, { useEffect, useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VisuallyHidden
} from '@chakra-ui/react';
import {
  FormFieldFiguresInput,
  FormFieldFileUpload
} from '@publishing-engine/domain/types/article';
import {
  ButtonIcon,
  ButtonText,
  Checkbox,
  FormFeedback,
  Input,
  useToast
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle,
  faPlus,
  faTrashAlt
} from '@fortawesome/pro-regular-svg-icons';
import { IFiguresValue } from 'src/pages/Organization/public/Journal/SubmissionUpload/components/Figures';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import SortableRow from './SortableRow';
import {
  restrictToParentElement,
  restrictToVerticalAxis
} from '@dnd-kit/modifiers';
import FormFieldFigureSingleFileUpload from './FormFieldFigureSingleFileUpload';
import { getValidationTypedMessage } from './common/errorMessages';
import { faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons';
import { IFiguresAndTablesFile } from '@publishing-engine/domain/models';
import SubFigures from './SubFigure';
import { DeleteFigureModal } from 'src/components/Submission/GenericFieldComponents/Figures/DeleteFigureModal';
import { FieldLabel } from '../Field';
import { ISubmissionFieldsResponse } from '@publishing-engine/submission-service/src/usecases/submission-fields/types';

const COLUMN_HELP_TEXT = {
  figureNo: `Auto-assigned from the figure's position in the list. Moving or removing a figure re-positions every figure below it in the list. In-text citations inside your manuscript file are not updated by the system, please make sure they match after any reorder.`,
  caption: `The legend that prints beneath the figure at typesetting. Omit the label and number; "Figure 1." is prepended for you.`,
  altText: `A text alternative served to screen readers, required under WCAG 2.1 SC 1.1.1. State what the figure shows and what it is there to demonstrate. Do not repeat the caption. One or two sentences describing the figure is expected. For a plot, give the trend or the finding rather than reading out every data point.`,
  composite: `Select this when a single file contains multiple panels that publish as one numbered figure. Panel labels (a, b, c) must already be present in the figure uploaded; they are not generated during production. If the file contains figures that should be numbered separately, split it and upload each one on its own.`,
  file: `Upload a high-resolution image (JPG, JPEG, or PNG, minimum 300 dpi, max 50 MB).`
} as const;

interface FormFieldFiguresProps {
  config: FormFieldFiguresInput;
  submissionId: string;
  defaultItem?: any;
  fields: any[];
  append: Function;
  remove: (index: number) => void;
  update: Function;
  selected: Record<string, boolean>;
  toggleRow: (id: string) => void;
  toggleSelectAll: () => void;
  methods: UseFormReturn<IFiguresValue>;
  move: (oldIndex: number, newIndex: number) => void; // passed from parent useFieldArray
  onLastFigureDeleted?: () => void;
  submissionFields?: ISubmissionFieldsResponse;
}

export interface SubFigureItem {
  id: string;
  caption: string;
  file: any | null;
  metadata?: any | null;
  altText?: string;
}

export type ParentFigure = IFiguresAndTablesFile[number];

export const formatMeta = (metaData: any) => {
  const formatted: Record<string, string> = {};

  if (metaData?.size !== null) {
    const sizeInMB = metaData.size / (1024 * 1024);
    formatted['Size'] =
      sizeInMB < 1
        ? `${(metaData.size / 1024).toFixed(2)} KB`
        : `${sizeInMB.toFixed(2)} MB`;
  }

  if (metaData?.width !== null) {
    formatted['Width'] = `${metaData.width}px`;
  }

  if (metaData?.height !== null) {
    formatted['Height'] = `${metaData.height}px`;
  }

  if (metaData?.pixels !== null) {
    formatted['Pixels'] = `${metaData.pixels.toLocaleString()} px²`;
  }

  if (metaData?.extension) {
    formatted['Extension'] = metaData.extension.toUpperCase();
  }

  return formatted;
};

const FormFieldFigures: React.FC<FormFieldFiguresProps> = ({
  config,
  submissionId,
  defaultItem = {
    caption: '',
    altText: '',
    file: null,
    metadata: null,
    isComposite: false
  },
  fields,
  append,
  remove,
  selected,
  toggleRow,
  toggleSelectAll,
  methods,
  move,
  onLastFigureDeleted,
  submissionFields
}) => {
  const {
    control,
    register,
    getValues,
    setValue,
    formState: { errors }
  } = methods;

  const toast = useToast();

  const [deleteFigureModal, setDeleteFigureModal] = useState(false);
  const [selectedFigureIndex, setSelectedFigureIndex] = useState(0);

  const {
    params: { validations },
    caseDataFields: { main: field }
  } = config;

  const parentSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  useEffect(() => {
    if (fields && fields.length === 0) {
      append(defaultItem);
    }
  }, [fields]);

  const onParentDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex(f => f.id === active.id);
    const newIndex = fields.findIndex(f => f.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    if (oldIndex !== newIndex) {
      move(oldIndex, newIndex);
    }
  };

  const handleDeleteSingleFigure = (index: number) => {
    const isLastFigure = fields.length <= 1;
    remove(index);
    setDeleteFigureModal(false);
    toast({
      variant: 'success',
      title: 'Figure deleted successfully.'
    });
    if (isLastFigure) {
      onLastFigureDeleted?.();
    }
  };

  const watchedFields = useWatch({
    control,
    name: 'fileSet.Figures'
  });

  const maxItems = validations?.maxItems ?? Number.POSITIVE_INFINITY;

  const totalItemsWithFile = Array.isArray(watchedFields)
    ? watchedFields.reduce((count, parent) => {
        const parentCount = !parent?.isComposite ? 1 : 0;
        const subCount = Array.isArray(parent?.subParts)
          ? parent.subParts.length
          : 0;

        return count + parentCount + subCount;
      }, 0)
    : 0;

  const disableAddMore = totalItemsWithFile >= maxItems;

  return (
    <Flex flexDirection="column" gap={{ base: 3, md: 2 }}>
      <DndContext
        sensors={parentSensors}
        collisionDetection={closestCenter}
        onDragEnd={onParentDragEnd}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      >
        <Box width="100%" overflowX={{ base: 'auto', md: 'visible' }}>
          <Table
            width="100%"
            minW={{ base: '720px', md: '100%' }}
            sx={{
              '& th, & td': {
                verticalAlign: 'top',
                px: { base: 2, md: 4 }
              }
            }}
          >
            <Thead>
              <Tr
                sx={{
                  '& th': {
                    textTransform: 'none',
                    color: 'black'
                  }
                }}
              >
                <Th width="1%">
                  <VisuallyHidden>Row actions</VisuallyHidden>
                </Th>
                <Th width="2%" ps={0}>
                  <>
                    <VisuallyHidden>Row actions</VisuallyHidden>
                    <Checkbox
                      ariaLabel="Select All"
                      id="check-all"
                      name="check-all"
                      isChecked={
                        fields.length > 0 && fields.every(f => selected[f.id])
                      }
                      isIndeterminate={
                        fields.some(f => selected[f.id]) &&
                        !fields.every(f => selected[f.id])
                      }
                      onChange={toggleSelectAll}
                    >
                      <VisuallyHidden>Select All</VisuallyHidden>
                    </Checkbox>
                  </>
                </Th>
                <Th ps={0} width="1%">
                  <FieldLabel
                    label="Figure No."
                    subText=""
                    isRequired={false}
                    helpText={COLUMN_HELP_TEXT.figureNo}
                  />
                </Th>
                <Th width="55%" ps={0}>
                  <Flex alignItems="center" gap={1}>
                    <FieldLabel
                      label="Caption"
                      subText=""
                      isRequired={false}
                      helpText={COLUMN_HELP_TEXT.caption}
                    />
                    <Text textStyle="headingItem">/</Text>
                    <FieldLabel
                      label="Alt-Text"
                      subText=""
                      isRequired={false}
                      helpText={COLUMN_HELP_TEXT.altText}
                    />
                  </Flex>
                </Th>
                <Th width="2%" ps={0}>
                  <FieldLabel
                    label="Composite"
                    subText=""
                    isRequired={false}
                    helpText={COLUMN_HELP_TEXT.composite}
                  />
                </Th>
                <Th width="28%" ps={0}>
                  <FieldLabel
                    label="File"
                    subText=""
                    isRequired={false}
                    helpText={COLUMN_HELP_TEXT.file}
                  />
                </Th>
                <Th width="5%" ps={0}>
                  <VisuallyHidden>Actions</VisuallyHidden>
                </Th>
              </Tr>
            </Thead>

            <SortableContext
              items={fields.map(f => f.id)}
              strategy={verticalListSortingStrategy}
            >
              <Tbody>
                {fields.map((item, index) => {
                  const captionName = `${field}[${index}].caption`;
                  const altTextName = `${field}[${index}].altText`;
                  const fileName = `${field}[${index}].file`;
                  const metaDataField = `${field}[${index}].metadata`;
                  const metaData = getValues(metaDataField as any);
                  const isCompositeField = `${field}[${index}].isComposite`;
                  const isComposite = !!getValues(isCompositeField as any);
                  const maxLength =
                    validations?.items?.properties?.caption?.maxLength ?? 1000;
                  const altTextMaxLength =
                    validations?.items?.properties?.altText?.maxLength ?? 1000;

                  const captionError =
                    errors?.fileSet?.Figures &&
                    Array.isArray(errors.fileSet.Figures) &&
                    errors.fileSet.Figures[index]?.caption;

                  const altTextError =
                    errors?.fileSet?.Figures &&
                    Array.isArray(errors.fileSet.Figures) &&
                    errors.fileSet.Figures[index]?.altText;

                  return (
                    <React.Fragment key={item.id}>
                      <SortableRow id={item.id} key={item.id}>
                        {({ attributes, listeners }: any) => (
                          <>
                            <Td {...attributes} {...listeners} cursor="grab">
                              <FontAwesomeIcon icon={faGripDotsVertical} />
                            </Td>

                            <Td ps={0}>
                              <Checkbox
                                ariaLabel={`Select figure ${index + 1}`}
                                id={`select-figure-${item.id}`}
                                name={`select-figure-${item.id}`}
                                isChecked={selected[item.id] || false}
                                onChange={() => toggleRow(item.id)}
                              >
                                <VisuallyHidden>Select Figure</VisuallyHidden>
                              </Checkbox>
                            </Td>

                            <Td ps={0}>
                              <Text>Figure {index + 1}</Text>
                            </Td>

                            <Td ps={0}>
                              <Flex
                                direction="column"
                                gap={{ base: 3, md: 4 }}
                                width="100%"
                              >
                                <Flex direction="column">
                                  <Input
                                    placeholder="Enter caption"
                                    {...register(captionName as any, {
                                      setValueAs: (value: string) =>
                                        value?.trim(),
                                      onBlur: e => {
                                        e.target.value = e.target.value.trim();
                                      }
                                    })}
                                    maxLength={maxLength}
                                    aria-label={captionName}
                                  />
                                  {captionError && (
                                    <Flex mt={2}>
                                      <FormFeedback variant="error">
                                        {getValidationTypedMessage(
                                          'Caption',
                                          validations.kind,
                                          validations,
                                          captionError
                                        )}
                                      </FormFeedback>
                                    </Flex>
                                  )}
                                </Flex>

                                <Flex direction="column">
                                  <Input
                                    placeholder="Enter alt-text"
                                    {...register(altTextName as any, {
                                      setValueAs: (value: string) =>
                                        value?.trim(),
                                      onBlur: e => {
                                        e.target.value = e.target.value.trim();
                                      }
                                    })}
                                    maxLength={altTextMaxLength}
                                    aria-label={altTextName}
                                  />
                                  {altTextError && (
                                    <Flex mt={2}>
                                      <FormFeedback variant="error">
                                        {getValidationTypedMessage(
                                          'Alt-Text',
                                          validations.kind,
                                          validations,
                                          altTextError
                                        )}
                                      </FormFeedback>
                                    </Flex>
                                  )}
                                </Flex>
                              </Flex>
                            </Td>

                            <Td ps={0}>
                              <Flex alignItems="flex-start" gap={3} mb={1}>
                                <Checkbox
                                  id={`composite-figure-${index}`}
                                  name={`composite-figure-${index}`}
                                  ariaLabel={`Composite image toggle for figure ${
                                    index + 1
                                  }`}
                                  isChecked={isComposite}
                                  onChange={e => {
                                    const checked = e.target.checked;
                                    const parentArray = (getValues(field) ||
                                      []) as IFiguresAndTablesFile;
                                    const parent: Partial<ParentFigure> =
                                      parentArray[index] ?? {};
                                    if (!parent) return;
                                    const rowPath = `${field}[${index}]`;
                                    const subParts =
                                      checked &&
                                      Array.isArray(parent.subParts) &&
                                      parent.subParts.length > 0
                                        ? parent.subParts
                                        : checked
                                          ? [
                                              {
                                                id: crypto.randomUUID(),
                                                caption: '',
                                                altText: '',
                                                file: null,
                                                metadata: null
                                              }
                                            ]
                                          : [];

                                    setValue(
                                      `${rowPath}.isComposite` as any,
                                      checked
                                    );
                                    setValue(`${rowPath}.file` as any, null);
                                    setValue(
                                      `${rowPath}.metadata` as any,
                                      null
                                    );
                                    setValue(
                                      `${rowPath}.subParts` as any,
                                      subParts
                                    );
                                  }}
                                >
                                  <VisuallyHidden>
                                    Composite Figure
                                  </VisuallyHidden>
                                </Checkbox>
                              </Flex>
                            </Td>

                            <Td ps={0}>
                              <Flex
                                direction="column"
                                gap={3}
                                align={'self-start'}
                              >
                                {isComposite && (
                                  <Tooltip
                                    label={`You have reached the maximum of ${maxItems} figures. Please delete an existing figure or move excess figures to the supplementary material.`}
                                    isDisabled={!disableAddMore}
                                    hasArrow
                                    placement="top"
                                  >
                                    <span>
                                      <ButtonText
                                        size="regular"
                                        iconLeft={
                                          <FontAwesomeIcon icon={faPlus} />
                                        }
                                        variant="secondary"
                                        disabled={disableAddMore}
                                        onClick={e => {
                                          e.preventDefault();
                                          const subPartsPath =
                                            `${field}[${index}].subParts` as any;
                                          const existing = (getValues(
                                            subPartsPath
                                          ) ?? []) as SubFigureItem[];

                                          setValue(subPartsPath, [
                                            ...existing,
                                            {
                                              id: crypto.randomUUID(),
                                              caption: '',
                                              altText: '',
                                              file: null,
                                              metadata: null
                                            }
                                          ]);
                                        }}
                                      >
                                        Add sub figure
                                      </ButtonText>
                                    </span>
                                  </Tooltip>
                                )}

                                {!isComposite && (
                                  <Box flex="1" width="100%">
                                    <FormFieldFigureSingleFileUpload
                                      submissionId={submissionId}
                                      submissionFields={submissionFields}
                                      submissionContextId={submissionId}
                                      methods={methods}
                                      config={
                                        {
                                          name: 'file',
                                          params: {
                                            validations: {
                                              required: true,
                                              maxSizeMB: validations.maxSizeMB,
                                              allowedFileFormats:
                                                validations.allowedFileFormats
                                            }
                                          },
                                          caseDataFields: { main: fileName }
                                        } as FormFieldFileUpload
                                      }
                                    />
                                  </Box>
                                )}
                              </Flex>
                            </Td>

                            <Td ps={0}>
                              <Flex width="100%" justify="flex-start" gap={2}>
                                <Box
                                  w="32px"
                                  display="flex"
                                  justifyContent="center"
                                >
                                  {metaData && (
                                    <Tooltip
                                      hasArrow
                                      placement="top"
                                      borderRadius={4}
                                      maxW="300px"
                                      p={3}
                                      bg="gray.50"
                                      color="black"
                                      fontSize="sm"
                                      whiteSpace="normal"
                                      data-cy="contributions-info-tooltip"
                                      label={
                                        <div>
                                          <strong
                                            style={{
                                              fontSize: '13px',
                                              display: 'block',
                                              marginBottom: '6px'
                                            }}
                                          >
                                            File Info
                                          </strong>

                                          {Object.entries(
                                            formatMeta(metaData)
                                          ).map(([key, value]) => (
                                            <div
                                              key={key}
                                              style={{
                                                marginBottom: '3px'
                                              }}
                                            >
                                              <span style={{ fontWeight: 600 }}>
                                                {key}:{' '}
                                              </span>
                                              {value}
                                            </div>
                                          ))}
                                        </div>
                                      }
                                    >
                                      <span>
                                        <ButtonIcon
                                          size="regular"
                                          ariaLabel="figures-meta-info"
                                          icon={
                                            <FontAwesomeIcon
                                              icon={faInfoCircle}
                                            />
                                          }
                                          data-cy="contributions-info-icon"
                                          ml={1}
                                          tabIndex={-1}
                                        />
                                      </span>
                                    </Tooltip>
                                  )}
                                </Box>
                                <Box w="32px">
                                  <ButtonIcon
                                    size="regular"
                                    ariaLabel={`Delete figure ${index + 1}`}
                                    icon={<FontAwesomeIcon icon={faTrashAlt} />}
                                    data-cy="delete-figure-icon"
                                    onClick={() => {
                                      setSelectedFigureIndex(index);
                                      setDeleteFigureModal(true);
                                    }}
                                  />
                                </Box>
                              </Flex>
                            </Td>
                          </>
                        )}
                      </SortableRow>

                      {/* If composite: sub-figures local DndContext (one per parent) */}
                      {isComposite && (
                        <>
                          <SubFigures
                            parentIndex={index}
                            field={field}
                            methods={methods}
                            validations={validations}
                            submissionId={submissionId}
                          />
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </Tbody>
            </SortableContext>
          </Table>
        </Box>
      </DndContext>

      {/* Add row button */}
      <Flex justifyContent={{ base: 'stretch', md: 'flex-end' }} mt={2}>
        <Tooltip
          label={`You have reached the maximum of ${maxItems} figures. Please delete an existing figure or move excess figures to the supplementary material.`}
          isDisabled={!disableAddMore}
          hasArrow
          placement="top"
        >
          <span>
            <ButtonText
              size="regular"
              iconLeft={<FontAwesomeIcon icon={faPlus} />}
              variant="secondary"
              onClick={() => append(defaultItem)}
              disabled={disableAddMore}
              _disabled={{ cursor: 'not-allowed', opacity: 0.6 }}
              width={{ base: '100%', sm: 'auto' }}
            >
              Add
            </ButtonText>
          </span>
        </Tooltip>
      </Flex>

      {deleteFigureModal && (
        <DeleteFigureModal
          onClose={() => setDeleteFigureModal(false)}
          isOpen={deleteFigureModal}
          onDelete={() => handleDeleteSingleFigure(selectedFigureIndex)}
          selectedFigures={{ [selectedFigureIndex]: true }}
        />
      )}
    </Flex>
  );
};

export default FormFieldFigures;
